import { useTheme } from "@emotion/react"
import styled from "@emotion/styled"
import Image from "next/image"
import Link from "next/link"
import { usePathname } from "next/navigation"
import React, { useEffect, useState } from "react"
import {
  extractMenuProperties,
  findProperty,
  getNonUmbracoUrl,
  getUmbracoMediaUrl,
  mainMenuClick,
} from "utils"
import PortNavLink from "../../NavLink/PortNavLink"
import { Desktop, Mobile, useIsDesktop } from "../../common/bit/MediaQueries"
import { Burger2Icon, CloseIcon } from "../../icons"
import SideMenu, { StyledButton, StyledLink, StyledList } from "./SideMenu"
import { HeaderCtaLink } from "./types"

const StyledRelative = styled.header`
  position: relative;
  z-index: 9999;
`

const StyledHeader = styled.div<{ isOpen: boolean }>`
  position: fixed;
  z-index: 100001;
  top: 0;
  left: 0;
  width: 100%;
  background-color: ${(props) => props.theme.colors.port.headerBg};
  color: ${(props) => props.theme.colors.navTextColor};
  display: flex;
  height: 80px;
  align-items: center;
  box-sizing: border-box;
  padding: 0;
  -webkit-backdrop-filter: blur(8px) saturate(120%) brightness(100%);
  backdrop-filter: blur(8px) saturate(120%) brightness(100%);
  @media (min-width: ${({ theme }) => theme.breakpoints.values.md}px) {
    height: 88px;
  }
`

const StyledContainer = styled.div`
  display: flex;
  justify-content: space-between;
  width: 100%;
  margin: auto;
  align-items: center;
  padding: 0 20px;
  gap: 8px;
  ${(props) => props.theme.breakpoints.up("md")} {
    gap: 16px;
    max-width: 1080px;
  }
  ${(props) => props.theme.breakpoints.up("lg")} {
    max-width: 1480px;
  }
`

const ListWrapper = styled.div`
  display: none;
  flex: 1;
  justify-content: center;
  ${(props) => props.theme.breakpoints.up("md")} {
    align-self: stretch;
    display: flex;
    align-items: stretch;
  }
`

const DesktopActions = styled.div`
  display: none;
  align-items: center;
  gap: 16px;
  ${(props) => props.theme.breakpoints.up("md")} {
    display: flex;
  }
`

const MobileActions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-left: auto;
  ${(props) => props.theme.breakpoints.up("md")} {
    display: none;
  }
`

const PrimaryCta = styled.a<{ as?: React.ElementType }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 45px;
  padding: 8px 16px;
  background-color: ${(props) => props.theme.colors.buttonDefaultBackgroundColor};
  color: ${(props) => props.theme.colors.heroTextColor};
  font-size: 18px;
  font-weight: 700;
  line-height: 1.5;
  text-decoration: none;
  white-space: nowrap;
  transition: background-color 0.2s ease-in-out;
  &:hover {
    background-color: ${(props) => props.theme.colors.buttonHoverBackgroundColor};
  }
  &:active {
    background-color: ${(props) => props.theme.colors.buttonActiveBackgroundColor};
  }
  &:focus-visible {
    outline: 2px solid ${(props) => props.theme.colors.buttonHoverBackgroundColor};
    outline-offset: 2px;
  }
  ${(props) => props.theme.breakpoints.up("md")} {
    padding: 10px 24px;
    font-size: 18px;
  }
`

const SecondaryCta = styled(PrimaryCta)`
  background-color: transparent;
  color: ${(props) => props.theme.colors.navTextColor};
  border: 1px solid ${(props) => props.theme.colors.buttonSecondaryBorderColor};
  &:hover {
    background-color: ${(props) => props.theme.colors.buttonSecondaryFilledBackground};
  }
  &:active {
    background-color: ${(props) => props.theme.colors.buttonSecondaryFilledBackground};
  }
`

type HeaderProps = {
  header_data: any
  locale: string
  data: any
  path?: any
  id?: string[]
  searchRoute?: any
  companyName: string
}

function Header({ locale, header_data, data, companyName }: HeaderProps) {
  const logo = findProperty(header_data, "logo")?.mediaItems?.[0]
  const theme = useTheme()
  const [open, setOpen] = React.useState<boolean>(false)
  const [openedDesktopMenuIndex, setOpenedDesktopMenuIndex] = React.useState<number>(-1)
  const pathname = usePathname() ?? "/"
  const isDesktop = useIsDesktop()
  const [, setSubMenuLink] = useState("")
  const primaryCta = findProperty(header_data, "blueButton")?.links?.[0] as
    | HeaderCtaLink
    | undefined
  const secondaryCta = findProperty(header_data, "phoneButton")?.links?.[0] as
    | HeaderCtaLink
    | undefined

  const createCtaElement = (cta: HeaderCtaLink | undefined, Component: typeof PrimaryCta) => {
    if (!cta?.url || !cta?.name) {
      return null
    }

    const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
      mainMenuClick(cta.name || "CTA", "")
      const href = getNonUmbracoUrl(cta.url || undefined)
      if (href.includes("#")) {
        e.preventDefault()
        const anchor = href.split("#")[1]
        const element = document.getElementById(anchor)
        if (element) {
          element.scrollIntoView({ behavior: "smooth" })
        }
      }
    }
    const target = cta.target ?? undefined
    const rel = target === "_blank" ? "noreferrer" : undefined
    const isInternal = cta.url.startsWith("/") && !cta.url.startsWith("//")

    let ctaName = cta.name || ""
    if (ctaName.includes("*")) {
      ctaName = ctaName.replace("*", "")
      ctaName = ctaName + "*"
    }
    if (isInternal) {
      return (
        <Component
          as={Link}
          href={getNonUmbracoUrl(cta.url)}
          target={target}
          rel={rel}
          onClick={handleClick}
        >
          {ctaName}
        </Component>
      )
    }

    return (
      <Component href={getNonUmbracoUrl(cta.url)} target={target} rel={rel} onClick={handleClick}>
        {ctaName}
      </Component>
    )
  }

  const renderPrimaryCta = () => createCtaElement(primaryCta, PrimaryCta)
  const renderSecondaryCta = () => createCtaElement(secondaryCta, SecondaryCta)

  useEffect(() => {
    if (open) {
      document.body.style.overflow = "hidden"
      document.documentElement.style.touchAction = "none"
      document.body.style.touchAction = "none"
    } else {
      document.documentElement.style.touchAction = "auto"
      document.body.style.overflow = "auto"
      document.body.style.touchAction = "auto"
    }

    return () => {
      document.body.style.overflow = "auto"
      document.documentElement.style.touchAction = "auto"
    }
  }, [open])

  const menuDesktop = (data?.menu_desktop ?? data)?.children

  return (
    <StyledRelative role="banner">
      <StyledHeader isOpen={open}>
        <StyledContainer>
          <Mobile
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "16px",
            }}
          >
            <StyledButton
              aria-expanded={open}
              aria-label={open ? "סגירת תפריט" : "פתיחת תפריט"}
              onClick={() => {
                setOpen((prev) => !prev)
                mainMenuClick(`${open ? "hamburger close" : "hamburger open"}`, "")
              }}
            >
              {open ? (
                <CloseIcon width={24} height={24} color={theme.colors.navTextColor} />
              ) : (
                <Burger2Icon width={24} height={24} color={theme.colors.navTextColor} />
              )}
            </StyledButton>
            <MobileActions>{renderPrimaryCta() || renderSecondaryCta()}</MobileActions>
          </Mobile>
          <Desktop>
            <StyledLink
              href={`/${locale === "en" ? "en" : ""}`}
              onClick={() => {
                mainMenuClick("Logo", "")
                setSubMenuLink("")
              }}
            >
              <Image
                style={{ objectFit: "contain", objectPosition: "right" }}
                src={getUmbracoMediaUrl(logo?.url)}
                alt={logo?.altText || `${companyName} logo`}
                width={99}
                height={61}
                priority
              />
            </StyledLink>
          </Desktop>
          <Mobile>
            <StyledLink
              href={`/${locale === "en" ? "en" : ""}`}
              onClick={() => {
                mainMenuClick("Logo", "")
                setSubMenuLink("")
              }}
            >
              <Image
                style={{ objectFit: "contain", objectPosition: "center" }}
                src={getUmbracoMediaUrl(logo?.url)}
                alt={logo?.altText || `${companyName} logo`}
                width={73}
                height={44}
                priority
              />
            </StyledLink>
          </Mobile>
          <ListWrapper>
            <StyledList role="navigation">
              {menuDesktop?.map((item: any, index: number) => {
                const decodedPathname = decodeURI(pathname) + "/"

                const subActive = item?.children?.some(
                  (sub: any) =>
                    getNonUmbracoUrl(extractMenuProperties(sub).url) === decodedPathname,
                )
                const subSubActive = item?.children?.some((sub: any) =>
                  sub?.children?.some(
                    (subSub: any) =>
                      getNonUmbracoUrl(extractMenuProperties(subSub).url) === decodedPathname,
                  ),
                )
                const { title, url, name } = item
                  ? extractMenuProperties(item)
                  : { title: "", url: "/", name: "" }
                return (
                  <PortNavLink
                    key={index.toString()}
                    href={getNonUmbracoUrl(url)}
                    selected={
                      decodedPathname === getNonUmbracoUrl(url) ||
                      (name?.length > 0 && decodeURI(pathname).includes(name?.toLowerCase())) ||
                      subActive ||
                      subSubActive
                    }
                    title={title}
                    subCategories={item?.children}
                    isOpen={isDesktop && openedDesktopMenuIndex === index}
                    setOpen={() => {
                      if (openedDesktopMenuIndex === index) {
                        setOpenedDesktopMenuIndex(-1)
                      } else {
                        setOpenedDesktopMenuIndex(index)
                      }
                    }}
                    onClick={() => {
                      setSubMenuLink(title)
                      setOpenedDesktopMenuIndex(-1)
                      mainMenuClick(title, "")
                    }}
                    setSubMenuLink={setSubMenuLink}
                    companyName={companyName}
                    color={theme.colors.navTextColor}
                    navlinkdropdownprops={{
                      background: theme.colors.port.headerBg,
                      WebkitBackdropFilter: "blur(8px) saturate(120%) brightness(100%)",
                      backdropFilter: "blur(8px) saturate(120%) brightness(100%)",
                    }}
                  />
                )
              })}
            </StyledList>
          </ListWrapper>

          <Desktop>
            <DesktopActions>
              {renderSecondaryCta()}
              {renderPrimaryCta()}
            </DesktopActions>
          </Desktop>
        </StyledContainer>
      </StyledHeader>
      <SideMenu
        menuItems={data}
        open={open}
        setOpen={setOpen}
        companyName={companyName}
        secondaryCta={(secondaryCta as any) || undefined}
      />
    </StyledRelative>
  )
}

export default Header
